import * as env from "@/utils/globals";

export class VacancyListFilter {
	private readonly filter: URLSearchParams;

	constructor(urlSearchParams?: URLSearchParams) {
		if(urlSearchParams != null) {
			// make a clone to prevent outside modifications.
			this.filter = env.getCleanedSearchParams(urlSearchParams);
			return;
		}

		this.filter = env.getSearchParams();
	}

	getFilter(): URLSearchParams {
		// make a clone to prevent outside modifications.
		return new URLSearchParams(this.filter);
	}

	getUrl(baseUrl?: string): string {
		let url = window.location.href.split(/[?#]/)[0];
		if(baseUrl != null && baseUrl.length > 0) {
			url = baseUrl;
		}
		return `${url}?${this.filter.toString()}`;
	}

	getUrlForShowMore(baseUrl?: string): string {
		let url = window.location.href.split(/[?#]/)[0];
		if(baseUrl != null && baseUrl.length > 0) {
			url = baseUrl;
		}
		let urlParams = this.filter;
		const excludedParams = ["rpp", "p", "bu", "vacancyCategory"];
		excludedParams.forEach(function (option) {
			if (urlParams.has(option)) {
				urlParams.delete(option);
			}
		});
		const filtersString = urlParams.toString();
		return filtersString.length > 0 ? `${url}?${filtersString}` : url;
	}

	clone(): VacancyListFilter {
		return new VacancyListFilter(this.filter);
	}

	setPage(pageNumber: number): this {
		this.filter.set("p", pageNumber.toString(10));
		return this;
	}

	getPage(): number {
		return parseInt(this.filter.get("p") || "1");
	}

	setResultsPerPage(numberOfVacancys: number): this {
		this.filter.set("rpp", numberOfVacancys.toString(10));
		return this;
	}

	getResultsPerPage(): number {
		return parseInt(this.filter.get("rpp") || "12");
	}

	setBusinessUnit(businessUnit: string): this {
		if (businessUnit.length <= 0) {
			return this;
		}

		this.filter.set("bu", businessUnit);
		return this;
	}

	getBusinessUnit(): string {
		return this.filter.get("bu") || "";
	}

	setVacancyCategory(category: string): this {
		if (category.length <= 0) {
			return this;
		}

		this.filter.set("vacancyCategory", category);
		return this;
	}

	getVacancyCategory(category: string): string {
		return this.filter.get("vacancyCategory") || "";
	}

	setQuery(searchQuery: string): this {
		if (searchQuery.length <= 0) {
			return this;
		}
		if (this.filter.has("q")) {
			let oldQuery = this.filter.get("q");
			if (searchQuery !== oldQuery) {
				// search changed: reset page to 1
				this.setPage(1);
			}
		}
		this.filter.set("q", searchQuery);
		return this;
	}

	clearQuery(): this {
		if (this.filter.has("q")) {
			this.filter.delete("q");
			this.setPage(1);
		}
		return this;
	}

	getQuery(): string {
		return this.filter.get("q") || "";
	}

	setFilterOptions(filterName: string, filterValues: string[]): this {
		if (filterName.length <= 0 || filterValues.length <= 0) {
			return this;
		}

		filterValues.forEach(filterValue => this.filter.append(filterName, filterValue));
		return this;
	}

	appendFilterOption(filterName: string, filterValue: string): this {
		if (filterName.length <= 0 || filterValue.length <= 0) {
			return this;
		}

		const currentOptions = this.getFilterOptions(filterName);
		if(currentOptions.includes(filterValue)) {
			return this;
		}

		this.filter.append(filterName, filterValue);
		// filter change: reset page to 1
		this.setPage(1);
		return this;
	}

	clearFilterOptions(filterName: string): this {
		// filter change: reset page to 1
		this.filter.delete(filterName);
		this.setPage(1);
		return this;
	}

	removeFilterOption(filterName: string, filterValue: string): this {
		const currentOptions = this.getFilterOptions(filterName);
		const adjustedOptions = currentOptions.filter(value => value !== filterValue);
		this.clearFilterOptions(filterName).setFilterOptions(filterName, adjustedOptions);
		// for filter change: see clearFilterOptions
		return this;
	}

	getAllFilterOptions(): {name: string, value: string}[] {
		// todo find a better way for this.
		const excludedOptions = ["rpp", "p", "q", "bu", "vacancyCategory"];
		return Array.from(this.filter.entries())
			.filter(([filterName, filterValue]: string[]) => !excludedOptions.includes(filterName))
			.map(([filterName, filterValue]: string[]) => ({name: filterName, value: filterValue}));
	}

	getFilterOptions(filterName: string): string[] {
		return this.filter.getAll(filterName) || [];
	}

}
